// Design template query/mutation hooks (templates.rs).
//   GET/POST /v1/workspaces/{ws}/templates, PATCH/DELETE /v1/templates/{id}
//
// Ported from ewt's templates.ts, minus the three copy fields (copyHeader/
// copyUnit/copyFooter) — those were ewt's display-page wording slots and
// have no surveys-widget analogue. PATCH and DELETE are scoped by
// templateId only on the wire, so callers pass workspaceId alongside for
// cache invalidation (same shape as surveys.ts).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { surveyKeys, templateKeys } from "./keys";
import type { Template } from "./types";

/**
 * Create/patch body for a template.
 *
 * Every field is tri-state on the wire, matching the server: omit to leave a
 * column alone, send `null` to clear it, send a value to set it.
 */
export interface TemplateBody {
    name?: string;
    backgroundColor?: string;
    backgroundImageAssetId?: string | null;
    logoAssetId?: string | null;
    logoPosition?: string;
    fontStack?: string | null;
    fontAssetId?: string | null;
    colorSlots?: Record<string, unknown>;
    customCss?: string | null;
    backgroundImageUrl?: string | null;
    logoUrl?: string | null;
    fontFaceUrl?: string | null;
}

export function useTemplates(workspaceId: string) {
    return useQuery({
        queryKey: templateKeys.list(workspaceId),
        queryFn: () => http.get<Template[]>(`/v1/workspaces/${workspaceId}/templates`),
        enabled: Boolean(workspaceId),
    });
}

export function useCreateTemplate(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TemplateBody) =>
            http.post<Template>(`/v1/workspaces/${workspaceId}/templates`, { json: body }),
        onSuccess: (created) => {
            // Seed the list cache so navigating straight into the new
            // template's page finds it without a not-found flash while the
            // invalidated list refetches.
            queryClient.setQueryData<Template[]>(templateKeys.list(workspaceId), (list) =>
                list ? [...list, created] : [created],
            );
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function usePatchTemplate(workspaceId: string, templateId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TemplateBody) => http.patch<Template>(`/v1/templates/${templateId}`, { json: body }),
        onSuccess: (updated) => {
            // Write the stored template into the list cache straight away: the
            // page reads its template from there, and Duplicate (enabled the
            // moment the save lands) must copy this, not the pre-save one.
            queryClient.setQueryData<Template[]>(templateKeys.list(workspaceId), (list) =>
                list?.map((t) => (t.id === updated.id ? updated : t)),
            );
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function useDeleteTemplate(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // The id arrives at mutate time so one hook instance serves a whole
        // list of rows.
        mutationFn: (templateId: string) => http.delete<void>(`/v1/templates/${templateId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
        // A refusal means a survey took the template since load: refresh its
        // count, and the surveys listed as using it (the Used by card),
        // awaited, so the caller reads the fresh reason once this settles.
        onError: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) }),
                queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) }),
            ]),
    });
}
